import { LoadingButton, type LoadingButtonProps } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

import Icon from 'components/Icon'
import { getPrivilegeForEndpoint } from 'context/auth-context-utils'
import toast from 'lib/toast'
import { type ApiProp } from 'utils/endpoints-access'
import { messageFromResponseError } from 'utils/strings'

export const DeactivateButton: React.FC<{
  apiProp: ApiProp
  dialogTitle: string
  id: string
  onConfirmRequest(): Promise<unknown>
}> &
  LoadingButtonProps = ({
  apiProp,
  dialogTitle,
  id,
  onConfirmRequest,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useBoolean(false)
  const [isSubmitting, setIsSubmitting] = useBoolean(false)

  const { endpoint, method } = apiProp

  const privilegeForEndpoint = getPrivilegeForEndpoint(endpoint, method)

  const handleOpenDialog = () => {
    setIsOpen(true)
  }

  const handleCloseDialog = () => {
    setIsOpen(false)
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)

    try {
      await onConfirmRequest()

      toast({
        type: 'success',
        text: 'Deactivation successful'
      })
      setIsSubmitting(false)
      setIsOpen(false)
    } catch (error) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          error,
          'Deactivation failed. Please try again later.'
        )
      })
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Button
        color="error"
        size="small"
        startIcon={<Icon icon="xmark" />}
        variant="contained"
        onClick={handleOpenDialog}
      >
        Deactivate
      </Button>
      <Dialog open={isOpen} onClose={handleCloseDialog}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent sx={{ justifyContent: 'center' }}>
          <Typography>
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {id}
            </Typography>{' '}
            will be deactivated and access from all Gremlin resources will be
            revoked.
          </Typography>
          <Typography>
            Note: only users with the{' '}
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {privilegeForEndpoint}
            </Typography>{' '}
            privilege may reactivate an agent once access is revoked.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            size="small"
            variant="contained"
            onClick={handleCloseDialog}
          >
            Cancel
          </Button>
          <LoadingButton
            color="error"
            data-testid={`deactivate-button-${id}`}
            loading={isSubmitting}
            size="small"
            startIcon={<Icon icon="xmark" />}
            variant="contained"
            onClick={handleSubmit}
            {...rest}
          >
            Deactivate
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
